import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, Phone, Check } from "lucide-react";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import QuoteForm from "@/components/QuoteForm";
import CTASection from "@/components/CTASection";
import LongForm from "@/components/LongForm";
import { deptContentBlocks } from "@/data/seoContent";
import { getDepartment, DEPARTMENTS, CONTACT } from "@/data/site";

export default function ZoneDepartment() {
  const { slug } = useParams();
  const dept = getDepartment(slug);

  if (!dept) return <Navigate to="/zone-intervention" replace />;

  const others = DEPARTMENTS.filter((d) => d.slug !== slug).slice(0, 4);

  return (
    <>
      <Seo
        title={`Recherche de Fuite d'Eau ${dept.name} (${dept.code}) | FuiteX`}
        description={`FuiteX, recherche de fuite d'eau non destructive dans le ${dept.name} (${dept.code}) : caméra thermique, acoustique, gaz traceur. Intervention rapide, devis gratuit.`}
        canonical={`/zone-intervention/${dept.slug}`}
      />
      <PageHero
        overline={`Département ${dept.code}`}
        titleLines={["Recherche de fuite d'eau", `${dept.name} (${dept.code})`]}
        subtitle={`Une fuite dans le ${dept.name} ? FuiteX localise l'origine sans destruction et intervient rapidement partout dans le département.`}
        breadcrumb={[{ label: "Zone d'intervention", to: "/zone-intervention" }, { label: dept.name }]}
      />

      <section className="bg-white py-24">
        <div className="container-x grid lg:grid-cols-12 gap-14 items-start">
          <div className="lg:col-span-7">
            <Reveal>
              <h2 className="text-3xl font-bold tracking-tight text-ink">
                Votre spécialiste de la fuite dans le {dept.name}
              </h2>
              <p className="mt-5 text-lg text-ink/70 leading-relaxed">
                Face à une fuite d'eau dans le {dept.name} ({dept.code}), chaque heure compte. FuiteX met à
                votre service des techniciens équipés d'un matériel de détection non destructif pour
                identifier précisément l'origine du problème — canalisation encastrée, piscine, chauffage
                au sol ou dégât des eaux — sans casser vos murs inutilement.
              </p>
              <p className="mt-4 text-lg text-ink/70 leading-relaxed">
                Nous couvrons l'ensemble du département et nous adaptons aux contraintes locales, aussi
                bien pour les particuliers que pour les syndics et professionnels du bâtiment.
              </p>

              <h3 className="mt-12 text-xl font-bold text-ink">Principales villes couvertes</h3>
              <div className="mt-4 flex flex-wrap gap-2.5">
                {dept.cities.map((c) => (
                  <span key={c} className="inline-flex items-center gap-2 rounded-full bg-mist px-4 py-2 text-sm font-semibold text-ink">
                    <Check size={14} className="text-aqua" /> {c}
                  </span>
                ))}
              </div>

              <h3 className="mt-12 text-xl font-bold text-ink">Intervention rapide, où que vous soyez</h3>
              <p className="mt-4 text-lg text-ink/70 leading-relaxed">
                Nous privilégions toujours les situations d'urgence. Un simple appel suffit pour organiser
                une recherche de fuite dans le {dept.name}, avec à la clé un compte-rendu détaillé, utile
                pour vos travaux comme pour votre dossier d'assurance.
              </p>

              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <a href={`tel:${CONTACT.phoneTel}`} className="btn-cta"><Phone size={18} /> {CONTACT.phoneDisplay}</a>
                <Link to="/services" className="btn-ghost">Voir nos services <ArrowRight size={16} /></Link>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.15} className="lg:col-span-5 lg:sticky lg:top-28">
            <QuoteForm defaultDepartment={`${dept.name} (${dept.code})`} />
          </Reveal>
        </div>
      </section>

      <section className="bg-white pb-24">
        <div className="container-x">
          <LongForm data={{ blocks: deptContentBlocks(dept) }} />
        </div>
      </section>

      <section className="bg-mist py-20">
        <div className="container-x">
          <h3 className="text-2xl font-bold tracking-tight text-ink mb-8">Autres départements couverts</h3>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {others.map((d) => (
              <Link key={d.slug} to={`/zone-intervention/${d.slug}`} className="group rounded-3xl border border-ink/10 bg-white p-6 hover:border-aqua/40 transition-colors">
                <span className="text-3xl font-black text-aqua">{d.code}</span>
                <h4 className="mt-3 font-bold text-ink group-hover:text-aqua transition-colors">{d.name}</h4>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </>
  );
}
